import { Box, Flex, Text, HStack, Icon, Badge } from "@chakra-ui/react";
import { Flag } from "lucide-react";
import { statusMeta } from "@/lib/submissionMeta";
import { displayStatus } from "@/lib/urgent";
import { ACK_META, ackStatus, signedCounts } from "@/components/AcknowledgeModal";
import { colors } from "@/theme/colors";

export const fmt = (d) => new Date(d).toLocaleString([], { dateStyle: "medium", timeStyle: "short" });
export const fmtDue = (d) => new Date(d + "T00:00:00").toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" });

const rowProps = {
  w: "full", textAlign: "left", px: "5", py: "3.5", gap: "3",
  direction: { base: "column", md: "row" }, align: { base: "flex-start", md: "center" },
  borderTop: "1px solid", borderColor: colors.border,
};

export function SubmissionRow({ r, fa, onOpen, company }) {
  const awaiting = fa?.status === "awaiting_signatures";
  const meta = statusMeta(displayStatus(r));
  const counts = fa ? signedCounts(fa) : null;
  return (
    <Flex as="button" type="button" onClick={onOpen} data-testid={`submission-row-${r.id}`}
      {...rowProps} _hover={{ bg: colors.page }} transition="background-color 0.15s">
      <Box flex="2.2">
        <HStack gap="1.5" flexWrap="wrap">
          <Text fontSize="sm" fontWeight="600" color={colors.textPrimary}>{r.template_title}</Text>
          {r.revision > 1 && <Badge bg={colors.tealTint} color={colors.tealHover} borderRadius="full" px="2" fontSize="10px">Rev {r.revision}</Badge>}
          {fa && fa.kind === "form" && <Badge bg="#FBEEDA" color="#8A5106" borderRadius="full" px="2" fontSize="10px" data-testid={`scheduled-badge-${r.id}`}>Scheduled</Badge>}
          {r.source === "contractor" && <Badge bg="#FBEEDA" color="#8A5106" borderRadius="full" px="2" fontSize="10px" data-testid={`contractor-badge-${r.id}`}>Contractor</Badge>}
          {r.source !== "contractor" && (r.contractor_names || []).length > 0 && <Badge bg="#FBEEDA" color="#8A5106" borderRadius="full" px="2" fontSize="10px" data-testid={`signoff-badge-${r.id}`}>Sign-off</Badge>}
          {r.flagged && <Icon as={Flag} boxSize="3.5" color={colors.poppy} data-testid={`flag-icon-${r.id}`} />}
        </HStack>
        <Text fontSize="xs" color={colors.textSecondary}>{r.template_type}</Text>
      </Box>
      {company != null && <Text flex="1.2" fontSize="sm" fontWeight="600" color={colors.tealHover} data-testid={`company-cell-${r.id}`}>{company}</Text>}
      <Text flex="1.4" fontSize="sm" color={colors.textPrimary}>{r.location_name}</Text>
      <Text flex="1.3" fontSize="sm" color={colors.textPrimary}>{r.worker_name}</Text>
      <Text flex="1.4" fontSize="sm" color={colors.textSecondary}>{fmt(r.created_at)}</Text>
      <Box w={{ base: "auto", md: "150px" }}>
        {awaiting ? (
          <Badge bg={ACK_META.awaiting_signatures.bg} color={ACK_META.awaiting_signatures.color} borderRadius="full" px="2.5"
            data-testid={`awaiting-badge-${r.id}`}>Awaiting signatures ({counts.signed} of {counts.total})</Badge>
        ) : (
          <Badge bg={meta.bg} color={meta.color} borderRadius="full" px="2.5">{meta.label}</Badge>
        )}
      </Box>
    </Flex>
  );
}

export function AckRow({ a, onOpen, company }) {
  const st = ackStatus(a);
  const meta = ACK_META[st];
  const { signed, total } = signedCounts(a);
  return (
    <Flex as="button" type="button" onClick={onOpen} data-testid={`ack-row-${a.id}`}
      {...rowProps} _hover={{ bg: colors.page }} transition="background-color 0.15s">
      <Box flex="2.2">
        <HStack gap="1.5" flexWrap="wrap">
          <Text fontSize="sm" fontWeight="600" color={colors.textPrimary}>{a.title}</Text>
          <Badge bg={colors.tealTint} color={colors.tealHover} borderRadius="full" px="2" fontSize="10px" data-testid={`ack-badge-${a.id}`}>Acknowledgement</Badge>
          {a.resource_version && <Badge bg={colors.cream} color={colors.textSecondary} borderRadius="full" px="2" fontSize="10px" data-testid={`ack-version-${a.id}`}>v{a.resource_version}</Badge>}
        </HStack>
        <Text fontSize="xs" color={colors.textSecondary}>Document sign-off</Text>
      </Box>
      {company != null && <Text flex="1.2" fontSize="sm" fontWeight="600" color={colors.tealHover} data-testid={`company-cell-${a.id}`}>{company}</Text>}
      <Text flex="1.4" fontSize="sm" color={colors.textPrimary}>{a.location_name || "—"}</Text>
      <Text flex="1.3" fontSize="sm" color={colors.textPrimary}>{a.worker_name}</Text>
      <Text flex="1.4" fontSize="sm" color={colors.textSecondary}>
        {a.completed_at ? fmt(a.completed_at) : `Due ${fmtDue(a.due_date)}`}
      </Text>
      <Box w={{ base: "auto", md: "150px" }}>
        <Badge bg={meta.bg} color={meta.color} borderRadius="full" px="2.5" data-testid={`ack-status-${a.id}`}>
          {st === "awaiting_signatures" ? `Awaiting signatures (${signed} of ${total})` : meta.label}
        </Badge>
      </Box>
    </Flex>
  );
}

export function AssignmentRow({ a, company }) {
  const st = ackStatus(a);
  const meta = ACK_META[st];
  return (
    <Flex data-testid={`assignment-row-${a.id}`} {...rowProps}>
      <Box flex="2.2">
        <HStack gap="1.5" flexWrap="wrap">
          <Text fontSize="sm" fontWeight="600" color={colors.textPrimary}>{a.title}</Text>
          <Badge bg="#FBEEDA" color="#8A5106" borderRadius="full" px="2" fontSize="10px" data-testid={`assignment-badge-${a.id}`}>Scheduled form</Badge>
        </HStack>
        <Text fontSize="xs" color={colors.textSecondary}>Form assignment — not yet submitted</Text>
      </Box>
      {company != null && <Text flex="1.2" fontSize="sm" fontWeight="600" color={colors.tealHover} data-testid={`company-cell-${a.id}`}>{company}</Text>}
      <Text flex="1.4" fontSize="sm" color={colors.textPrimary}>{a.location_name || "—"}</Text>
      <Text flex="1.3" fontSize="sm" color={colors.textPrimary}>{a.worker_name}</Text>
      <Text flex="1.4" fontSize="sm" color={colors.textSecondary}>Due {fmtDue(a.due_date)}</Text>
      <Box w={{ base: "auto", md: "150px" }}>
        <Badge bg={meta.bg} color={meta.color} borderRadius="full" px="2.5" data-testid={`assignment-status-${a.id}`}>{meta.label}</Badge>
      </Box>
    </Flex>
  );
}
